import {
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CardWrapper } from '../../Common/CommonComponents';
import {
  BaseDragableProps,
  GroupState,
  PermissionState,
  PortalPermission,
} from '../Model/AccessControlModel';

interface DragableUIProps {
  onDragEnd: (result: DragEndEvent) => void;
  liststate: PermissionState | GroupState;
  sourceList: string;
  destListTitle: string;
}

export function DragableUI({
  onDragEnd,
  liststate,
  sourceList,
  destListTitle,
}: DragableUIProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" padding={1}>
              {sourceList}
            </Typography>
            <Divider sx={{ mt: 2, mb: 2 }} />
            <BaseDragable
              listId="unselectedlist"
              items={liststate.unselectedlist}
            />
          </CardWrapper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" padding={1}>
              {destListTitle}
            </Typography>
            <Divider sx={{ mt: 2, mb: 2 }} />
            <BaseDragable
              listId="selectedlist"
              items={liststate.selectedlist}
            />
          </CardWrapper>
        </Grid>
      </Grid>
    </DndContext>
  );
}

const BaseDragable: React.FC<BaseDragableProps> = ({ listId, items }) => {
  const itemIds = items.map((item, index) => `${listId}-${index}`);

  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <List
        sx={{
          height: { xs: 280, md: 420 },
          width: '100%',
          maxWidth: '100%',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          overflowY: 'auto',
          padding: 1,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: 'center', py: 2 }}
          >
            No items available
          </Typography>
        ) : (
          items.map((item, index) => (
            <SortableItem
              key={`${listId}-${index}`}
              id={`${listId}-${index}`}
              item={item}
            />
          ))
        )}
      </List>
    </SortableContext>
  );
};

interface SortableItemProps {
  id: string;
  item: string | PortalPermission;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, item }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        backgroundColor: 'primary.light',
        mb: 1,
        borderRadius: 1,
        padding: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <CustomListText item={item} />
    </ListItem>
  );
};

const CustomListText: React.FC<{ item: string | PortalPermission }> = ({
  item,
}) => {
  if (typeof item === 'string') {
    return <ListItemText primary={item} />;
  }
  return <ListItemText primary={item.PermissionDisplayName} />;
};
